import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LoadingStatus } from "../sessions/models/LoadingStatus";
import { ApiErrorResponse } from "../../api/types/ApiErrorResponse";
import { RootState } from "../../app/store";
import { Instructor } from "./models/Instructor";
import {
  fetchColleagues,
  fetchCurrentInstructor,
  updateInstructorLicenceNumber,
  updateInstructorStatus,
  updateInstructorOrganizationRole,
} from "./slice-extensions/instructors-slice-extension";

interface InstructorsState {
  colleagues: Instructor[];
  /** The signed-in instructor's own profile, once loaded. */
  current?: Instructor;
  initializationStatus: "not-initialized" | "initializing" | "initialized";
  status: LoadingStatus;
  lastError?: ApiErrorResponse;
}

const initialState: InstructorsState = {
  colleagues: [],
  initializationStatus: "not-initialized",
  status: LoadingStatus.Idle,
  lastError: undefined,
};

const upsertColleague = (state: InstructorsState, instructor: Instructor) => {
  const index = state.colleagues.findIndex((c) => c.id === instructor.id);
  if (index >= 0) {
    state.colleagues[index] = instructor;
  } else {
    state.colleagues.push(instructor);
  }
};

const instructorsSlice = createSlice({
  name: "instructors",
  initialState,
  reducers: {
    clearInstructorsError: (state) => {
      state.lastError = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchColleagues.pending, (state) => {
        state.status = LoadingStatus.Loading;
        state.initializationStatus = "initializing";
        state.lastError = undefined;
      })
      .addCase(
        fetchColleagues.fulfilled,
        (state, action: PayloadAction<Instructor[]>) => {
          state.status = LoadingStatus.Idle;
          state.initializationStatus = "initialized";
          state.colleagues = action.payload;
        }
      )
      .addCase(fetchColleagues.rejected, (state, action) => {
        state.status = LoadingStatus.Failed;
        state.initializationStatus = "initialized";
        state.lastError = action.payload;
      });

    builder
      .addCase(fetchCurrentInstructor.fulfilled, (state, action) => {
        state.current = action.payload;
      })
      .addCase(updateInstructorLicenceNumber.fulfilled, (state, action) => {
        state.current = action.payload;
        if (state.colleagues.some((c) => c.id === action.payload.id)) {
          upsertColleague(state, action.payload);
        }
      });

    builder
      .addCase(updateInstructorStatus.pending, (state) => {
        state.status = LoadingStatus.Loading;
        state.lastError = undefined;
      })
      .addCase(updateInstructorStatus.fulfilled, (state, action) => {
        state.status = LoadingStatus.Idle;
        upsertColleague(state, action.payload);
      })
      .addCase(updateInstructorStatus.rejected, (state, action) => {
        state.status = LoadingStatus.Failed;
        state.lastError = action.payload;
      });

    builder
      .addCase(updateInstructorOrganizationRole.pending, (state) => {
        state.status = LoadingStatus.Loading;
        state.lastError = undefined;
      })
      .addCase(updateInstructorOrganizationRole.fulfilled, (state, action) => {
        state.status = LoadingStatus.Idle;
        upsertColleague(state, action.payload);
      })
      .addCase(updateInstructorOrganizationRole.rejected, (state, action) => {
        state.status = LoadingStatus.Failed;
        state.lastError = action.payload;
      });
  },
});

export const { clearInstructorsError } = instructorsSlice.actions;

export const selectColleagues = (state: RootState) =>
  state.instructors.colleagues;

export const selectActiveColleagues = createSelector(
  [selectColleagues],
  (colleagues) => colleagues.filter((c) => c.isActive)
);

export const selectCurrentInstructor = (state: RootState) =>
  state.instructors.current;

export const selectColleaguesInitializationStatus = (state: RootState) =>
  state.instructors.initializationStatus;

export const selectInstructorsStatus = (state: RootState) =>
  state.instructors.status;

export const selectInstructorsError = (state: RootState) =>
  state.instructors.lastError;

export default instructorsSlice.reducer;
